<!-- Modals -->

<!-- Warning modal when moving node from private drive to public drive -->
<ui-confirmation-dialog
    #publicDataForbiddenDialog
    header="document.publicDataForbiddenHeader"
    primaryButtonLabel="document.publicDataForbiddenButton"
>
    {{ 'document.publicDataForbidden' | translate }}
</ui-confirmation-dialog>

<!-- Warning modal when moving view-only doc from public drive to private drive -->
<ui-confirmation-dialog
    #privateViewOnlyDocForbiddenDialog
    header="document.privateViewOnlyDocForbiddenHeader"
    primaryButtonLabel="document.privateViewOnlyDocForbiddenButton"
>
    {{ 'document.privateViewOnlyDocForbidden' | translate }}
</ui-confirmation-dialog>

<ui-confirmation-dialog #moveErrorResult [header]="moveErrorHeader" primaryButtonLabel="Ok">
    {{ moveErrorDescription | translate }}
    <ul>
        <li *ngFor="let conflict of moveConflicts">{{ conflict }}</li>
    </ul>
</ui-confirmation-dialog>

<!-- Rename folder modal -->
<ui-dialog
    size="small"
    header="{{ 'document.renameFolder' | translate }}"
    [open]="folderRenameDialogOpen"
    [form]="renameFolder"
    primaryButtonLabel="{{ 'document.rename' | translate }}"
    secondaryButtonLabel="cancel"
    (closing)="folderRename = ''; folderRenameDialogOpen = false"
    (primaryButtonPressed)="renameFolder.valid && onRenameFolder()"
    (secondaryButtonPressed)="folderRenameDialogOpen = false; folderRename = ''"
>
    <form #renameFolder="ngForm" class="flex col">
        <mat-form-field>
            <mat-label>{{ 'document.folderName' | translate }}</mat-label>
            <input
                matInput
                required
                name="folderName"
                placeholder="{{ 'document.newFolder' | translate }}"
                [(ngModel)]="folderRename"
            />
        </mat-form-field>
    </form>
</ui-dialog>

<!-- Delete folder modal -->
<ui-dialog
    size="small"
    header="{{ 'document.folderDeletion.permanentlyDeleteFolder' | translate }}"
    [open]="deleteFolderDialogOpen"
    (closing)="deleteFolderDialogOpen = false"
>
    <div>
        <p>{{ 'document.folderDeletion.warning' | translate }}</p>
        @if (nodeToDelete?.driveType === driveTypeEnum.private) {
            <ul>
                <li>
                    {{ 'document.folderDeletion.signingTemplateWarning' | translate }}
                </li>
                <li>
                    {{ 'document.folderDeletion.acknowledgementWarning' | translate }}
                </li>
                <li>
                    {{ 'document.folderDeletion.offerLetterWarning.deletingWill' | translate }}
                </li>
                <ul>
                    <li>
                        {{ 'document.folderDeletion.offerLetterWarning.withdrawOffers' | translate }}
                    </li>
                    <li>
                        {{ 'document.folderDeletion.offerLetterWarning.deleteSignatures' | translate }}
                    </li>
                </ul>
            </ul>
        }
        <mat-checkbox #canDeleteCheckbox>
            <span>{{ 'document.iUnderstandThisCannotBeUndone' | translate }}</span>
        </mat-checkbox>
    </div>
    <ui-dialog-footer>
        <ui-button type="ghost" (click)="deleteFolderDialogOpen = false">{{ 'document.cancel' | translate }}</ui-button>
        <ui-button type="danger" [disabled]="!canDeleteCheckbox.checked" (click)="onDeleteFolder()">{{
            'document.deletePermanently' | translate
        }}</ui-button>
    </ui-dialog-footer>
</ui-dialog>

<h2 class="mobile-title">{{ 'document.folders' | translate }}</h2>
<div class="sidebar-container" [class.loading]="isLoading" class="loadable">
    <!-- Body -->
    <div class="folders-sidebar-content">
        <!-- Sidebar Tree with drives and folders -->
        <div *ngIf="sidebarData.length" class="tree">
            <!-- Fake drives (mobile view only) -->
            @if (hasRecentDocumentsListener()) {
                <div
                    class="tree-title cdk-node-item hide-large"
                    [class.isCurrentNode]="inRecentDocumentsState"
                    (click)="onRecentDocuments()"
                >
                    <mat-icon class="icon drive-icon" svgIcon="clockSimple"></mat-icon>
                    <span class="drive-title-color">{{ 'account.recentDocuments' | translate }}</span>
                </div>
            }
            @if (hasIncompleteDocumentsListener()) {
                <div
                    class="tree-title cdk-node-item hide-large"
                    [class.isCurrentNode]="isIncompleteView"
                    (click)="onIncompleteDocument()"
                >
                    <mat-icon class="icon drive-icon" svgIcon="edit"></mat-icon>
                    <span class="drive-title-color">{{ 'account.signingRequests' | translate }}</span>
                </div>
            }
            <ng-container *ngFor="let drive of sidebarData; let i = index">
                <!-- Drives -->
                <div
                    appDroppable
                    (onDroppableComplete)="onDrop($event, drive)"
                    class="tree-title cdk-node-item"
                    [attr.data-id]="drive.nodeId"
                    [class.isCurrentNode]="!deselectEverything && tableParentNode.id === drive.nodeId"
                    role="tree"
                    [attr.aria-label]="driveTypeDisplay[drive.driveType] | translate"
                    [attr.aria-owns]="'folderGroup' + drive.nodeId"
                    (click)="loadTable(drive.nodeId)"
                >
                    @if (isMobile) {
                        <!-- Drive icon (mobile view only) -->
                        @switch (drive.driveType) {
                            @case (driveTypeEnum.private) {
                                <mat-icon class="icon private drive-icon" svgIcon="drive"></mat-icon>
                            }
                            @case (driveTypeEnum.employee) {
                                <mat-icon class="icon private drive-icon" svgIcon="drive"></mat-icon>
                            }
                            @case (driveTypeEnum.public) {
                                <mat-icon class="icon public drive-icon" svgIcon="drive"></mat-icon>
                            }
                        }
                    }
                    <div
                        role="button"
                        [class.only-large]="true"
                        class="expand-toggle-icon drop-hover-highlight"
                        (click)="toggleNodeExpanded(drive); $event.stopPropagation()"
                    >
                        <mat-icon *ngIf="drive.expanded" class="icon" svgIcon="solidChevronDown"></mat-icon>
                        <mat-icon *ngIf="!drive.expanded" class="icon" svgIcon="solidChevronRight"></mat-icon>
                    </div>
                    <span role="link" class="drive-title-color">{{
                        driveTypeDisplay[drive.driveType] | translate
                    }}</span>
                </div>
                <!-- Folders -->
                <div class="drive-tree">
                    <ng-container *ngIf="(drive.expanded && drive.children) || isMobile">
                        <div *ngFor="let node of drive.children" role="group" id="{{ 'folderGroup' + drive.nodeId }}">
                            <ng-container
                                *ngTemplateOutlet="
                                    treeTemplate;
                                    context: {
                                        node: node,
                                        nodeIcon: getNodeIcon(node),
                                        depth: depth,
                                    }
                                "
                            ></ng-container>
                        </div>
                    </ng-container>
                </div>
            </ng-container>
        </div>
    </div>
</div>

<!-- Tree Template -->
<ng-template #treeTemplate let-node="node" let-nodeIcon="nodeIcon" let-depth="depth">
    <div
        [class.expanded]="node.expanded"
        class="cdk-node-item"
        [attr.data-id]="node.nodeId"
        [attr.id]="'node-' + node.nodeId"
    >
        <div
            appDraggable
            [draggableData]="node"
            [dragEnabled]="allowDragAndDrop"
            appDroppable
            [dropEnabled]="allowDragAndDrop"
            (onDroppableComplete)="onDrop($event, node)"
            class="node-title"
            [class.isCurrentNode]="!deselectEverything && tableParentNode.id === node.nodeId"
            [ngStyle]="{ 'padding-left': 'calc(+' + (depth > 12 ? 12 : depth) + '*8px + 16px)' }"
            (click)="loadTable(node.nodeId)"
        >
            <div
                [class.only-large]="!node.children.length"
                class="expand-toggle-icon drop-hover-highlight"
                (click)="toggleNodeExpanded(node); $event.stopPropagation()"
            >
                <mat-icon *ngIf="node.expanded" class="icon" svgIcon="solidChevronDown"></mat-icon>
                <div class="only-large">
                    <mat-icon *ngIf="!node.expanded" class="icon" svgIcon="solidChevronRight"></mat-icon>
                </div>
                <div class="hide-large">
                    <mat-icon *ngIf="!node.expanded" class="icon" svgIcon="chevronUp"></mat-icon>
                </div>
            </div>

            <mat-icon class="icon folder-icon drop-hover-highlight" [svgIcon]="nodeIcon"></mat-icon
            ><span role="treeitem" class="node-name-color drop-hover-highlight">{{
                getNodeDisplayName(node) | translate
            }}</span>

            <div *ngIf="showMeatballs" class="node-meatball">
                <div class="context-menu">
                    <button
                        (click)="$event.stopPropagation(); node.activeState = true"
                        class="material-meatball-menu-button"
                        [matMenuTriggerFor]="folderMenu"
                        #meatballTrigger="matMenuTrigger"
                    >
                        <mat-icon class="horizontal" svgIcon="meatballHorizontal"></mat-icon>
                        <mat-icon class="vertical" svgIcon="meatballVertical"></mat-icon>
                    </button>

                    <mat-menu #folderMenu="matMenu" xPosition="before">
                        <span (mouseleave)="meatballTrigger.closeMenu(); node.activeState = false">
                            <button
                                mat-menu-item
                                (click)="
                                    $event.stopPropagation();
                                    folderRenameDialogOpen = true;
                                    nodeToRename = node;
                                    folderRename = node.name;
                                    meatballTrigger.closeMenu()
                                "
                            >
                                {{ 'document.rename' | translate }}
                            </button>
                            <button
                                mat-menu-item
                                (click)="
                                    $event.stopPropagation();
                                    deleteFolderDialogOpen = true;
                                    nodeToDelete = node;
                                    meatballTrigger.closeMenu()
                                "
                            >
                                {{ 'document.delete' | translate }}
                            </button>
                        </span>
                    </mat-menu>
                </div>
            </div>
        </div>

        <div class="node-children">
            <ng-container *ngIf="node.expanded && node.children.length">
                <div *ngFor="let child of node.children" role="group">
                    <ng-container
                        *ngTemplateOutlet="
                            treeTemplate;
                            context: { node: child, nodeIcon: getNodeIcon(child), depth: depth + 1 }
                        "
                    ></ng-container>
                </div>
            </ng-container>
        </div>
    </div>
</ng-template>
